import { useGetDailyAppointment } from "@/services/appointment/hooks/useGetDailyAppointment";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import useScheduleStore from "@/store/schedule";
import {
  ActionIcon,
  Grid,
  Group,
  LoadingOverlay,
  Stack,
  Text,
} from "@mantine/core";
import { useScrollIntoView } from "@mantine/hooks";
import { IconCaretLeftFilled, IconPlus } from "@tabler/icons-react";
import moment from "moment-jalaali";
import React, { useEffect, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppointmentBadge from "./components/appointment";
import "./DailyCalendar.css"; // For custom styles
import { getHourlyAppointments } from "./helper";
import { notifications } from "@mantine/notifications";

const DailyCalendar: React.FC = () => {
  const { selectedDate, setDate, merchantServiceFilters, showType } =
    useScheduleStore();

  const navigate = useNavigate();

  const { data, isLoading } = useGetDailyAppointment({
    fromDate: moment(selectedDate).format("YYYY-MM-DD"),
    toDate: moment(selectedDate).format("YYYY-MM-DD"),
  });
  const { data: merchantServices, isSuccess } = useGetMerchantServices();

  const calendarWithAppointments = useMemo(() => {
    if (data) {
      if (merchantServiceFilters && merchantServiceFilters?.length > 0) {
        const filteredData = {
          ...data,
          // TODO:fixed
          dailyServiceItems: data?.filter(({ user }) =>
            merchantServiceFilters?.some((x) => (x.id || 0) === (user?.id || 0))
          ),
        };
        console.log(filteredData);
        return getHourlyAppointments({ day: 1 }, merchantServices || []);
      }

      return getHourlyAppointments({ day: 1 }, merchantServices || []);
    }
  }, [data, merchantServices, merchantServiceFilters]);

  const timeNowHour = moment().format("H:00");
  const { scrollIntoView, targetRef, scrollableRef } = useScrollIntoView<
    HTMLDivElement,
    HTMLDivElement
  >();

  const isToday =
    new Date().getDate() === new Date(new Date(selectedDate))?.getDate();

  useLayoutEffect(() => {
    if (isToday) {
      scrollIntoView();
    }
  }, [scrollIntoView, isToday, targetRef, calendarWithAppointments, showType]);

  useEffect(() => {
    setDate({
      selectedDate: new Date(),
    });
  }, []);

  useEffect(() => {
    if (isSuccess && merchantServices?.length === 0) {
      notifications.show({
        message: "لطفا ابتدا خدمات مربوط به نوبت دهی خود را مشخص نمایید",
        color: "yellow",
      });
      navigate("/dashboard/schedule/services/add/isNew", {
        state: {},
        replace: true,
      });
    }
  }, [isSuccess, merchantServices]);

  return (
    <>
      <Stack
        ref={scrollableRef as React.RefObject<HTMLDivElement>}
        h={"calc(100vh - 230px)"}
        style={{ overflowY: "scroll" }}
        px="md"
        id="panel"
      >
        <LoadingOverlay visible={isLoading} />
        <Stack gap="0">
          {showType === "daily"
            ? calendarWithAppointments?.map((hour) => (
                <Stack
                  key={hour?.id}
                  gap="0"
                  py="sm"
                  m="0"
                  bg={isToday && timeNowHour === hour.title ? "orange.0" : ""}
                  styles={{
                    root: {
                      borderBottom: "1px solid var(--mantine-color-gray-4)",
                    },
                  }}
                  ref={
                    timeNowHour === hour.title
                      ? (targetRef as React.RefObject<HTMLDivElement>)
                      : null
                  }
                >
                  <Grid>
                    <Grid.Col span={2}>
                      <Group
                        justify="center"
                        align="center"
                        h="100%"
                        wrap="nowrap"
                        gap="0"
                      >
                        <Text size="xs" c="dimmed">
                          {hour.title}
                        </Text>
                        {isToday && timeNowHour === hour.title && (
                          <IconCaretLeftFilled color="orange" />
                        )}
                      </Group>
                    </Grid.Col>
                    <Grid.Col span={9}>
                      <Group gap="5">
                        {hour?.appointments?.map((x) => (
                          <AppointmentBadge
                            appointment={x}
                            isToday={isToday}
                            selectedDate={selectedDate}
                            key={x.id}
                          />
                        ))}
                      </Group>
                    </Grid.Col>
                    <Grid.Col span={1}>
                      <Group justify="center" align="center" h="100%">
                        <ActionIcon
                          color="green.4"
                          size="xs"
                          variant="light"
                          onClick={() =>
                            navigate("/dashboard/schedule/add", {
                              state: {
                                date: {
                                  time: hour.title,
                                  day: selectedDate,
                                },
                              },
                            })
                          }
                        >
                          <IconPlus />
                        </ActionIcon>
                      </Group>
                    </Grid.Col>
                  </Grid>
                </Stack>
              ))
            : calendarWithAppointments
                ?.flatMap((x) => x.appointments)
                .map((item) => (
                  <Stack mb="xs">
                    <Group gap="5">
                      <AppointmentBadge
                        appointment={item}
                        isToday={isToday}
                        selectedDate={selectedDate}
                        key={item?.id}
                      />
                    </Group>
                  </Stack>
                ))}
        </Stack>
      </Stack>
    </>
  );
};

export default DailyCalendar;
